import { CalendarPlus, Video } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { createRoom } from "@/lib/api/rooms";
import { formatRoomCode, isValidRoomCode } from "@/lib/room";
import { useT } from "@/lib/i18n";

export function QuickStart({ onSchedule }: { onSchedule: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const t = useT();

  const start = async () => {
    setBusy(true);
    try {
      const room = await createRoom({ title: t("dash.instantTitle") });
      void navigate(`/${room.slug}`);
    } catch (error) {
      toast.fail(error);
    } finally {
      setBusy(false);
    }
  };

  const valid = isValidRoomCode(code);

  return (
    <div className="flex flex-col gap-3 rounded-panel border border-line bg-surface p-5 shadow-panel sm:flex-row sm:items-end">
      <div className="flex flex-1 flex-wrap gap-3">
        <Button
          loading={busy}
          onClick={() => {
            void start();
          }}
          size="lg"
        >
          <Video className="h-4 w-4" /> {t("dash.startNow")}
        </Button>
        <Button onClick={onSchedule} size="lg" variant="secondary">
          <CalendarPlus className="h-4 w-4" /> {t("dash.scheduleLater")}
        </Button>
      </div>

      <form
        className="flex w-full items-end gap-2 sm:w-auto"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) void navigate(`/${formatRoomCode(code)}`);
        }}
      >
        <TextField
          className="font-mono tracking-widest sm:w-52"
          hideLabel
          label={t("landing.meetingCode")}
          onChange={(event) => {
            setCode(event.target.value);
          }}
          placeholder={t("landing.codePlaceholder")}
          value={code}
        />
        <Button disabled={!valid} size="lg" type="submit" variant="secondary">
          {t("common.join")}
        </Button>
      </form>
    </div>
  );
}
